import React, { useMemo, useRef } from 'react';
import { EventSnapshot } from '../../api/types';
import { getCountryFlagUrl } from '../../utils/flagUtils';
import { TopKombis } from './TopKombis/TopKombis';
import { CategoryMatchList } from './CategoryMatchList/CategoryMatchList';
import { PreMatchList } from './PreMatchList/PreMatchList';
import { LiveMatchList } from './LiveMatchList/LiveMatchList';
import DesktopMyBetsPage from '../DesktopMyBetsPage';
import { TOP_COMPETITIONS, SPORT_EMOJI } from './DesktopLeftSidebar';
import { TOURNAMENT_ID_MAP } from '../../constants/tipico_menu';
import { useTranslation } from '../../i18n';
import { TopEventsCard } from './TopEventsCard';
import { Market, Outcome, TopEventItem } from '../../api/types';

export interface DesktopCenterContentProps {
  activePage: string;
  loading: boolean;
  filteredPre: EventSnapshot[];
  filteredLive: EventSnapshot[];
  topPreEvents: EventSnapshot[];
  topLiveEvents: EventSnapshot[];
  topEventsApiData: TopEventItem[];
  activePreSport: string;
  setActivePreSport: (s: string) => void;
  activeLiveSport: string;
  setActiveLiveSport: (s: string) => void;
  allPreSportTabs?: { id: string; name: string; count: number; emoji: string }[];
  quickFilter: string | null;
  setQuickFilter: (f: string | null) => void;
  topMarket1: string | number;
  setTopMarket1: (m: string | number) => void;
  topMarket2: string | number;
  setTopMarket2: (m: string | number) => void;
  topMarket3: string | number;
  setTopMarket3: (m: string | number) => void;
  topMarket4: string | number;
  setTopMarket4: (m: string | number) => void;
  topMarket5: string | number;
  setTopMarket5: (m: string | number) => void;
  lastMinMarket1: string | number;
  setLastMinMarket1: (m: string | number) => void;
  lastMinMarket2: string | number;
  setLastMinMarket2: (m: string | number) => void;
  lastMinMarket3: string | number;
  setLastMinMarket3: (m: string | number) => void;
  lastMinMarket4: string | number;
  setLastMinMarket4: (m: string | number) => void;
  lastMinMarket5: string | number;
  setLastMinMarket5: (m: string | number) => void;
  liveMarket1: string | number;
  setLiveMarket1: (m: string | number) => void;
  liveMarket2: string | number;
  setLiveMarket2: (m: string | number) => void;
  liveMarket3: string | number;
  setLiveMarket3: (m: string | number) => void;
  liveMarket4: string | number;
  setLiveMarket4: (m: string | number) => void;
  liveMarket5: string | number;
  setLiveMarket5: (m: string | number) => void;
  upcomingMarket1: string | number;
  setUpcomingMarket1: (m: string | number) => void;
  upcomingMarket2: string | number;
  setUpcomingMarket2: (m: string | number) => void;
  upcomingMarket3: string | number;
  setUpcomingMarket3: (m: string | number) => void;
  upcomingMarket4: string | number;
  setUpcomingMarket4: (m: string | number) => void;
  upcomingMarket5: string | number;
  setUpcomingMarket5: (m: string | number) => void;
  openDropdown: string | null;
  setOpenDropdown: (val: string | null | ((prev: string | null) => string | null)) => void;
  preMarketOptions: (string | number)[];
  liveMarketOptions: (string | number)[];
  onOddsClick?: (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => void;
  isSelected?: (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) => boolean;
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string, isUp?: boolean, isDown?: boolean) => React.ReactNode;
  handleEventClick: (ev: EventSnapshot) => void;
  formatTime: (iso: string) => string;
  formatClock: (ev: EventSnapshot) => string;
  onNavigate: (id: string) => void;
  onLoginClick: () => void;

  allLiveSportTabs: { id: string; name: string; count: number; emoji: string }[];
  sortBy: 'zeit' | 'wettbewerb' | 'oddsAsc' | 'oddsDesc';
  setSortBy: (s: 'zeit' | 'wettbewerb' | 'oddsAsc' | 'oddsDesc') => void;
  liveSportEvents: EventSnapshot[];
  tournamentGroups: Record<string, EventSnapshot[]>;
  sorted: EventSnapshot[];
  next6h: EventSnapshot[];
  columnHeaders: React.ReactNode;
  upcomingColumnHeaders?: React.ReactNode;
  renderLiveOddsRow: (ev: EventSnapshot) => React.ReactNode;
  renderNext6hRow: (ev: EventSnapshot) => React.ReactNode;
  hasSelectedEvent?: boolean;
  hasActiveFilters?: boolean;
  activeStreams?: Set<string>;
  loadingMoreLive?: boolean;
  onLoadMoreLiveUpcoming?: () => void;
}

const MemoLiveRow = React.memo(({ ev, renderRow }: { ev: EventSnapshot, renderRow: (ev: EventSnapshot) => React.ReactNode }) => {
  return <>{renderRow(ev)}</>;
}, (prev, next) => prev.ev === next.ev && prev.renderRow === next.renderRow);

const MemoNext6hRow = React.memo(({ ev, renderRow }: { ev: EventSnapshot, renderRow: (ev: EventSnapshot) => React.ReactNode }) => {
  return <>{renderRow(ev)}</>;
}, (prev, next) => prev.ev === next.ev && prev.renderRow === next.renderRow);

export const DesktopCenterContent: React.FC<DesktopCenterContentProps> = ({
  activePage, loading, filteredPre, filteredLive, topPreEvents, topLiveEvents, topEventsApiData = [], activePreSport, setActivePreSport, activeLiveSport, setActiveLiveSport, allPreSportTabs = [], quickFilter, setQuickFilter,
  topMarket1, setTopMarket1, topMarket2, setTopMarket2, topMarket3, setTopMarket3, topMarket4, setTopMarket4, topMarket5, setTopMarket5,
  lastMinMarket1, setLastMinMarket1, lastMinMarket2, setLastMinMarket2, lastMinMarket3, setLastMinMarket3, lastMinMarket4, setLastMinMarket4, lastMinMarket5, setLastMinMarket5,
  liveMarket1, setLiveMarket1, liveMarket2, setLiveMarket2, liveMarket3, setLiveMarket3, liveMarket4, setLiveMarket4,
  liveMarket5, setLiveMarket5,
  upcomingMarket1, setUpcomingMarket1,
  upcomingMarket2, setUpcomingMarket2,
  upcomingMarket3, setUpcomingMarket3,
  upcomingMarket4, setUpcomingMarket4,
  upcomingMarket5, setUpcomingMarket5,
  openDropdown,
  setOpenDropdown,
  preMarketOptions,
  liveMarketOptions,
  onOddsClick,
  isSelected,
  renderMarketColHeader, renderMarketColCells,
  handleEventClick, formatTime, formatClock,
  onNavigate, onLoginClick, allLiveSportTabs,
  sortBy, setSortBy, liveSportEvents,
  tournamentGroups, sorted, next6h,
  columnHeaders, upcomingColumnHeaders, renderLiveOddsRow, renderNext6hRow, hasSelectedEvent, hasActiveFilters, activeStreams = new Set(),
  loadingMoreLive, onLoadMoreLiveUpcoming
}) => {
  const { t } = useTranslation();

  const filteredTopPreEvents = useMemo(() => {
    return topPreEvents.filter((ev) => ev.event.sport_id === activePreSport).slice(0, 60);
  }, [topPreEvents, activePreSport]);

  const filteredTopLiveEvents = useMemo(() => {
    return topLiveEvents.filter((ev) => ev.event.sport_id === activeLiveSport).slice(0, 60);
  }, [topLiveEvents, activeLiveSport]);

  const groupedNext6h = useMemo(() => {
    return next6h.reduce((acc, ev) => {
      const t = ev.event.tournament_name;
      if (!acc[t]) acc[t] = [];
      acc[t].push(ev);
      return acc;
    }, {} as Record<string, EventSnapshot[]>);
  }, [next6h]);

  const topEvents = useMemo(() => {
    return topEventsApiData;
  }, [topEventsApiData]);

  const sliderRef = useRef<HTMLDivElement>(null);
  const scrollSlider = (dir: 'left' | 'right') => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
    }
  };

  const getLocalizedSportName = (type: 'pre' | 'live') => {
    const id = type === 'pre' ? activePreSport : activeLiveSport;
    const tabs = type === 'pre' ? allPreSportTabs : allLiveSportTabs;
    const tab = tabs.find(t => t.id === id);
    return tab ? tab.name : id;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-tipico-red border-t-transparent" />
      </div>
    );
  }

  if (activePage === 'live') {
    return (
      <div className="flex flex-col gap-6">
        {allLiveSportTabs.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {allLiveSportTabs.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveLiveSport(s.id)}
                className={`relative min-w-[70px] px-2 h-[56px] flex-shrink-0 bg-white rounded shadow-sm border-b-[3px] transition group ${activeLiveSport === s.id
                    ? 'border-[#cc0000]'
                    : 'border-transparent hover:border-gray-300'
                  }`}
              >
                <span className="absolute top-1 right-1.5 text-[11px] font-bold text-gray-400 group-hover:text-gray-600 transition">
                  {s.count}
                </span>
                <div className="flex flex-col items-center justify-center h-full pt-1">
                  <span className="text-xl leading-none">{s.emoji}</span>
                  <span className={`text-[12px] mt-1 font-medium whitespace-nowrap ${activeLiveSport === s.id ? 'text-gray-900 font-bold' : 'text-gray-600'}`}>
                    {s.name}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {liveSportEvents.length === 0 ? (
          <div className="bg-white rounded shadow-sm p-10 text-center text-gray-400 text-[15px] font-medium">
            Keine Live-Ereignisse für {getLocalizedSportName('live')}.
          </div>
        ) : (
          <div>
            <h2 className="text-[15px] font-bold text-gray-900 mb-3 px-1">{getLocalizedSportName('live')}</h2>
            <div className="bg-white rounded shadow-sm border border-gray-200 relative z-20">
              {columnHeaders}
              {sortBy === 'wettbewerb'
                ? Object.entries(tournamentGroups).map(([tournament, events]) => (
                  <React.Fragment key={tournament}>
                    <div className="px-5 py-1.5 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center justify-between mt-[-1px]">
                      <div className="flex items-center gap-2">
                        {(() => { const flagUrl = getCountryFlagUrl(events[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-6 h-[18px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                        <span className="text-[12px] font-bold text-gray-900">{tournament}</span>
                        <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z" /></svg>
                      </div>
                      <div className="flex items-center text-[13px] font-bold text-gray-400">
                        {renderMarketColHeader(liveMarket1, "w-[151px]")}
                        <div className={hasSelectedEvent ? "hidden min-[1500px]:block" : ""}>{renderMarketColHeader(liveMarket2, "w-[151px]")}</div>
                        <div className={hasSelectedEvent ? "hidden min-[1650px]:block" : "hidden min-[1500px]:block"}>{renderMarketColHeader(liveMarket3, "w-[151px]")}</div>
                        {!hasSelectedEvent && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(liveMarket4, "w-[151px]")}</div>}
                        {!hasSelectedEvent && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(liveMarket5, "w-[151px]")}</div>}
                        <div className="w-[52px]" />
                      </div>
                    </div>
                    {events.map(ev => <MemoLiveRow key={ev.event_id} ev={ev} renderRow={renderLiveOddsRow} />)}
                  </React.Fragment>
                ))
                : sorted.map(ev => <MemoLiveRow key={ev.event_id} ev={ev} renderRow={renderLiveOddsRow} />)
              }
            </div>
          </div>
        )}

        {next6h.length > 0 && (
          <div>
            <h2 className="text-[15px] font-bold text-gray-900 mb-3 px-1">{t('section.next6Hours') || 'Nächste 6 Stunden'}</h2>
            <div className="bg-white rounded shadow-sm border border-gray-200 relative z-10">
              {upcomingColumnHeaders}
              {Object.entries(groupedNext6h).map(([tournament, eventsGroup]) => (
                <React.Fragment key={tournament}>
                  <div className="px-5 py-1.5 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center justify-between mt-[-1px]">
                    <div className="flex items-center gap-2">
                      {(() => { const flagUrl = getCountryFlagUrl(eventsGroup[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-6 h-[18px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                      <span className="text-[12px] font-bold text-gray-900">{tournament}</span>
                      <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z" /></svg>
                    </div>
                    <div className="flex items-center text-[13px] font-bold text-gray-400">
                      {renderMarketColHeader(upcomingMarket1 || topMarket1, "w-[151px]")}
                      <div className={hasSelectedEvent ? "hidden min-[1500px]:block" : ""}>{renderMarketColHeader(upcomingMarket2 || topMarket2, "w-[151px]")}</div>
                      <div className={hasSelectedEvent ? "hidden min-[1650px]:block" : "hidden min-[1500px]:block"}>{renderMarketColHeader(upcomingMarket3 || topMarket3, "w-[151px]")}</div>
                      {!hasSelectedEvent && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(upcomingMarket4 || topMarket4, "w-[151px]")}</div>}
                      {!hasSelectedEvent && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(upcomingMarket5 || topMarket5, "w-[151px]")}</div>}
                      <div className="w-[52px]" />
                    </div>
                  </div>
                  {eventsGroup.map(ev => <MemoNext6hRow key={ev.event_id} ev={ev} renderRow={renderNext6hRow} />)}
                </React.Fragment>
              ))}
              
              <div className="flex justify-center p-4">
                {loadingMoreLive ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-600 border-t-transparent" />
                    <span className="ml-2 text-sm text-gray-500 font-medium">Lade weitere Ereignisse...</span>
                  </div>
                ) : (
                  <button
                    onClick={onLoadMoreLiveUpcoming}
                    className="px-6 py-2 bg-[#f2f2f2] hover:bg-gray-200 text-gray-700 text-sm font-bold rounded transition"
                  >
                    Weitere Ereignisse laden
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activePage === 'chats' || activePage === 'bets') {
    return <DesktopMyBetsPage onLoginClick={onLoginClick} />;
  }

  return (
    <>
      {/* 0. Top Events View: Render horizontal scrolling cards */}
      {!quickFilter && !hasActiveFilters && topEvents.length > 0 && (
        <div className="mb-4">

          <div className="relative w-full group/slider px-2">
            <button 
              onClick={() => scrollSlider('left')}
              className="absolute left-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/slider:opacity-100 transition-opacity hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            <div className="relative flex-1 min-w-0 px-2 pb-2">
              <div ref={sliderRef} className="flex gap-4 overflow-x-auto scrollbar-hide snap-x flex-1">
                {topEvents.map((ev) => (
                  <div key={ev.event_id} className="snap-start flex-shrink-0">
                    <TopEventsCard 
                      event={ev} 
                      onOddsClick={onOddsClick} 
                      isSelected={isSelected} 
                      formatTime={formatTime} 
                    />
                  </div>
                ))}
              </div>
            </div>

            <button 
              onClick={() => scrollSlider('right')}
              className="absolute right-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/slider:opacity-100 transition-opacity hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 1. Default Last Minute View: Render at the top of the middle area on default home page */}
      {!quickFilter && !hasActiveFilters && topPreEvents.length > 0 && (
        <PreMatchList
          title={t('section.lastMinute')}
          hideSecondMarket={hasSelectedEvent}
          events={filteredTopPreEvents}
          activeSport={activePreSport}
          setActiveSport={setActivePreSport}
          sportsTab={allPreSportTabs.map((t) => ({ id: t.id, name: t.name }))}
          topMarket1={lastMinMarket1}
          setTopMarket1={setLastMinMarket1}
          topMarket2={lastMinMarket2}
          setTopMarket2={setLastMinMarket2}
          topMarket3={lastMinMarket3}
          setTopMarket3={setLastMinMarket3}
          topMarket4={lastMinMarket4}
          setTopMarket4={setLastMinMarket4}
          topMarket5={lastMinMarket5}
          setTopMarket5={setLastMinMarket5}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          options={preMarketOptions}
          renderMarketColHeader={renderMarketColHeader}
          renderMarketColCells={renderMarketColCells}
          handleEventClick={handleEventClick}
          formatTime={formatTime}
          showViewAll={false}
        />
      )}

      {/* 2. Live Match List: Render below Last Minute on default home page */}
      {!quickFilter && !hasActiveFilters && allLiveSportTabs.length > 0 && (
        <LiveMatchList
          title={t('section.live')}
          hideSecondMarket={hasSelectedEvent}
          events={filteredTopLiveEvents}
          activeSport={activeLiveSport}
          setActiveSport={setActiveLiveSport}
          sportsTab={allLiveSportTabs.map((t) => ({ id: t.id, name: t.name }))}
          liveMarket1={liveMarket1}
          setLiveMarket1={setLiveMarket1}
          liveMarket2={liveMarket2}
          setLiveMarket2={setLiveMarket2}
          liveMarket3={liveMarket3}
          setLiveMarket3={setLiveMarket3}
          liveMarket4={liveMarket4}
          setLiveMarket4={setLiveMarket4}
          liveMarket5={liveMarket5}
          setLiveMarket5={setLiveMarket5}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          options={liveMarketOptions}
          renderMarketColHeader={renderMarketColHeader}
          renderMarketColCells={renderMarketColCells}
          handleEventClick={handleEventClick}
          formatClock={formatClock}
          showViewAll={true}
          onViewAll={() => onNavigate('live')}
          activeStreams={activeStreams}
        />
      )}

      {/* 3. Filter / Category / Active Search Views */}
      {(quickFilter || hasActiveFilters) && (
        filteredPre.length > 0 ? (
          (quickFilter?.startsWith('category:') || quickFilter?.startsWith('tournament:')) ? (
            <CategoryMatchList
              events={filteredPre}
              hideSecondMarket={hasSelectedEvent}
              categoryPrefix={quickFilter?.startsWith('category:') ? (quickFilter.split(':')[1] + ' / ') : quickFilter?.startsWith('tournament:') ? (filteredPre[0]?.event?.category_name + ' / ') : ''}
              hasTabs={quickFilter?.startsWith('tournament:')}
              topMarket1={topMarket1}
              setTopMarket1={setTopMarket1}
              topMarket2={topMarket2}
              setTopMarket2={setTopMarket2}
              topMarket3={topMarket3}
              setTopMarket3={setTopMarket3}
              topMarket4={topMarket4}
              setTopMarket4={setTopMarket4}
              topMarket5={topMarket5}
              setTopMarket5={setTopMarket5}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              options={preMarketOptions}
              renderMarketColHeader={renderMarketColHeader}
              renderMarketColCells={renderMarketColCells}
              handleEventClick={handleEventClick}
              formatTime={formatTime}
            />
          ) : (
            <PreMatchList
              title={hasActiveFilters ? getLocalizedSportName('pre') : (quickFilter === 'todays-matches' ? t('sidebar.todayMatches') : t('section.topEvents'))}
              hideSecondMarket={hasSelectedEvent}
              events={filteredPre}
              activeSport={activePreSport}
              setActiveSport={setActivePreSport}
              sportsTab={[]}
              topMarket1={topMarket1}
              setTopMarket1={setTopMarket1}
              topMarket2={topMarket2}
              setTopMarket2={setTopMarket2}
              topMarket3={topMarket3}
              setTopMarket3={setTopMarket3}
              topMarket4={topMarket4}
              setTopMarket4={setTopMarket4}
              topMarket5={topMarket5}
              setTopMarket5={setTopMarket5}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              options={preMarketOptions}
              renderMarketColHeader={renderMarketColHeader}
              renderMarketColCells={renderMarketColCells}
              handleEventClick={handleEventClick}
              formatTime={formatTime}
              showViewAll={false}
            />
          )
        ) : (
          hasActiveFilters && (
            <div className="bg-white rounded shadow-sm p-10 text-center text-gray-500 text-[15px] font-medium border border-gray-200">
              {t('misc.noMatches')}
            </div>
          )
        )
      )}
    </>
  );
};